import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Filter, 
  MessageSquare, 
  Clock, 
  User, 
  MapPin, 
  Monitor, 
  RefreshCw,
  X,
  Send
} from 'lucide-react';

const SupportDashboard = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    dateRange: '',
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [response, setResponse] = useState('');
  const [stats, setStats] = useState({
    totalMessages: 0,
    pendingMessages: 0,
    resolvedMessages: 0,
    averageResponseTime: '0h',
  });

  // Fetch messages
  const fetchMessages = async () => {
    setLoading(true);
    try {
      // Call your backend endpoint
      const response = await fetch(`/uss/messages`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const data = await response.json();
      setMessages(data.messages || []);
      setTotalPages(data.total_pages || 1);
      
      // Update stats
      setStats({
        totalMessages: data.total_messages || 0,
        pendingMessages: data.pending_messages || 0,
        resolvedMessages: data.resolved_messages || 0,
        averageResponseTime: data.average_response_time || '0h',
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters/page changes
  useEffect(() => {
    fetchMessages();
  }, [currentPage, filters]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMessages();
  };

  // Handle filter changes
  const applyFilters = () => {
    setCurrentPage(1);
    fetchMessages();
    setShowFilterMenu(false);
  };

  // Handle response submission
  const handleSubmitResponse = async () => {
    if (!response.trim()) return;
    
    try {
      const result = await fetch(`/messages/${selectedMessage.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ response }),
      });
      
      if (!result.ok) {
        throw new Error('Failed to submit response');
      }
      
      // Refresh messages after response
      fetchMessages();
      setSelectedMessage(null);
      setResponse('');
      
    } catch (err) {
      setError(err.message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-indigo-400">Support Dashboard</h1>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => fetchMessages()} 
              className="p-2 rounded-full hover:bg-gray-700 transition"
              title="Refresh data"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-6">
        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow">
            <h3 className="text-gray-400 text-sm font-medium">Total Messages</h3>
            <p className="text-2xl font-bold text-white mt-1">{stats.totalMessages}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow">
            <h3 className="text-gray-400 text-sm font-medium">Pending</h3>
            <p className="text-2xl font-bold text-yellow-400 mt-1">{stats.pendingMessages}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow">
            <h3 className="text-gray-400 text-sm font-medium">Resolved</h3>
            <p className="text-2xl font-bold text-green-400 mt-1">{stats.resolvedMessages}</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 shadow">
            <h3 className="text-gray-400 text-sm font-medium">Avg. Response Time</h3>
            <p className="text-2xl font-bold text-blue-400 mt-1">{stats.averageResponseTime}</p>
          </div>
        </div>

        {/* Search and filter */}
        <div className="flex flex-col lg:flex-row justify-between mb-6 gap-4">
          <form onSubmit={handleSearch} className="flex w-full lg:w-1/2">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search messages..."
                className="w-full rounded-l-lg bg-gray-800 border border-gray-700 py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
            <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 px-4 rounded-r-lg">
              Search
            </button>
          </form>
          
          <div className="relative">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="flex items-center gap-2 bg-gray-800 border border-gray-700 hover:bg-gray-700 px-4 py-2 rounded-lg"
            >
              <Filter size={18} />
              <span>Filters</span>
            </button>
            
            {showFilterMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10">
                <div className="p-4">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                    <select
                      className="w-full bg-gray-700 border border-gray-600 rounded-md py-1 px-2"
                      value={filters.status}
                      onChange={(e) => setFilters({...filters, status: e.target.value})}
                    >
                      <option value="">All</option>
                      <option value="Pending">Pending</option>
                      <option value="Resolved">Resolved</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Service</label>
                    <select
                      className="w-full bg-gray-700 border border-gray-600 rounded-md py-1 px-2"
                      value={filters.service}
                      onChange={(e) => setFilters({...filters, service: e.target.value})}
                    >
                      <option value="">All</option>
                      <option value="Account Setup">Account Setup</option>
                      <option value="Technical Support">Technical Support</option>
                      <option value="Billing">Billing</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-1">Date Range</label>
                    <select
                      className="w-full bg-gray-700 border border-gray-600 rounded-md py-1 px-2"
                      value={filters.dateRange}
                      onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                    >
                      <option value="">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                    </select>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-md"
                      onClick={applyFilters}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
            <div className="flex items-center">
              <span className="mr-2">⚠️</span>
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Messages table */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden shadow mb-6">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                <p className="mt-4 text-gray-400">Loading messages...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-30" />
              <p>No messages found matching your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-700/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Message</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {messages.map((message) => (
                    <tr key={message.id} className="hover:bg-gray-700/40 transition">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-indigo-400">#{message.id}</td>
                      <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">{message.message}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium">{message.user.full_name.split(' ').map(n => n[0]).join('')}</span>
                          </div>
                          <div className="ml-3">
                            <p className="text-white text-sm font-medium">{message.user.full_name}</p>
                            <p className="text-gray-400 text-xs">{message.user.company_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{message.service.name}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          message.status === 'Pending' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-green-900/30 text-green-400'
                        }`}>
                          {message.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{formatDate(message.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <div className="flex items-center">
                          <MapPin size={14} className="mr-1 text-gray-400" />
                          {message.location.city}, {message.location.country}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <button 
                          onClick={() => setSelectedMessage(message)}
                          className="text-indigo-400 hover:text-indigo-300"
                        >
                          View & Respond
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-400">
            {messages.length > 0 && (
              <>Showing page {currentPage} of {totalPages}</>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-md border ${
                currentPage === 1 
                  ? 'border-gray-700 text-gray-500 cursor-not-allowed' 
                  : 'border-gray-600 hover:bg-gray-700 text-gray-300'
              }`}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-md border ${
                currentPage === totalPages 
                  ? 'border-gray-700 text-gray-500 cursor-not-allowed' 
                  : 'border-gray-600 hover:bg-gray-700 text-gray-300'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </main>

      {/* Message detail modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-white">Message Details</h3>
              <button 
                onClick={() => setSelectedMessage(null)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-400">Ticket #{selectedMessage.id}</p>
                    <h4 className="text-lg font-medium text-white mt-1">{selectedMessage.service.name}</h4>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedMessage.status === 'Pending' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-green-900/30 text-green-400'
                  }`}>
                    {selectedMessage.status}
                  </span>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-start mb-3">
                    <div className="flex-shrink-0 h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
                      <span className="text-xs font-medium">{selectedMessage.user.full_name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div>
                      <div className="flex items-center">
                        <p className="font-medium text-white">{selectedMessage.user.full_name}</p>
                        <span className="mx-2 text-gray-500">•</span>
                        <p className="text-sm text-gray-400">{formatDate(selectedMessage.created_at)}</p>
                      </div>
                      <p className="text-gray-300 mt-2">{selectedMessage.message}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-400 mb-3">User Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <User size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">Email:</span> {selectedMessage.user.email}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <User size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">Phone:</span> {selectedMessage.user.phone}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <Monitor size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">Browser:</span> {selectedMessage.browser} {selectedMessage.browser_version}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <Monitor size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">OS:</span> {selectedMessage.os} {selectedMessage.os_version}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <MapPin size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">Location:</span> {selectedMessage.location.city}, {selectedMessage.location.region}, {selectedMessage.location.country}
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3">
                    <div className="flex items-center">
                      <Clock size={16} className="text-gray-400 mr-2" />
                      <p className="text-sm text-gray-300">
                        <span className="text-gray-400">Created:</span> {formatDate(selectedMessage.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">Respond to User</h4>
                <div className="mb-3">
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Type your response here..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg p-3 text-gray-200 min-h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  ></textarea>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-700 flex justify-end">
              <button
                onClick={() => setSelectedMessage(null)}
                className="mr-3 px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitResponse}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white flex items-center"
                disabled={!response.trim()}
              >
                <Send size={16} className="mr-2" />
                Send Response
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDashboard;